var _excluded = ["strengthMultiplier", "verticalStrength", "horizontalStrength", "onScrollChange"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import defaults from 'defaults';
import { DndContext } from 'react-dnd';
import hoist from 'hoist-non-react-statics';
import { noop } from './util.js';
import ScrollingMonitor from './ScrollingMonitor.js';
var DEFAULT_BUFFER = 150;
var getDisplayName = function getDisplayName(PassedComponent) {
  return PassedComponent.displayName || PassedComponent.name || (typeof PassedComponent === 'string' && PassedComponent.length > 0 ? PassedComponent : 'Unknown');
};
export function createHorizontalStrength(_buffer) {
  return function defaultHorizontalStrength(_ref, point) {
    var x = _ref.x,
      w = _ref.w,
      y = _ref.y,
      h = _ref.h;
    var buffer = Math.min(w / 2, _buffer);
    var inRange = point.x >= x && point.x <= x + w;
    var inBox = inRange && point.y >= y && point.y <= y + h;
    if (inBox) {
      if (point.x < x + buffer) {
        return (point.x - x - buffer) / buffer;
      } else if (point.x > x + w - buffer) {
        return -(x + w - point.x - buffer) / buffer;
      }
    }
    return 0;
  };
}
export function createVerticalStrength(_buffer) {
  return function defaultVerticalStrength(_ref2, point) {
    var y = _ref2.y,
      h = _ref2.h,
      x = _ref2.x,
      w = _ref2.w;
    var buffer = Math.min(h / 2, _buffer);
    var inRange = point.y >= y && point.y <= y + h;
    var inBox = inRange && point.x >= x && point.x <= x + w;
    if (inBox) {
      if (point.y < y + buffer) {
        return (point.y - y - buffer) / buffer;
      } else if (point.y > y + h - buffer) {
        return -(y + h - point.y - buffer) / buffer;
      }
    }
    return 0;
  };
}
export var defaultHorizontalStrength = createHorizontalStrength(DEFAULT_BUFFER);
export var defaultVerticalStrength = createVerticalStrength(DEFAULT_BUFFER);
var defaultOptions = {
  onScrollChange: noop,
  verticalStrength: defaultVerticalStrength,
  horizontalStrength: defaultHorizontalStrength,
  strengthMultiplier: 30
};
export function useDndScrolling(componentRef, passedOptions) {
  var _useContext = useContext(DndContext),
    dragDropManager = _useContext.dragDropManager;
  if (!dragDropManager) {
    throw new Error('Unable to get dragDropManager from context. Wrap this in <DndProvider>.');
  }
  useEffect(function () {
    if (!componentRef.current) {
      return function () {};
    }
    var options = defaults(passedOptions, defaultOptions);
    var monitor = new ScrollingMonitor(dragDropManager, componentRef.current, options);
    monitor.start();
    return function () {
      monitor.stop();
    };
  }, [componentRef, dragDropManager, passedOptions]);
}
export default function createScrollingComponent(WrappedComponent) {
  function ScrollingComponent(_ref3) {
    var strengthMultiplier = _ref3.strengthMultiplier,
      verticalStrength = _ref3.verticalStrength,
      horizontalStrength = _ref3.horizontalStrength,
      onScrollChange = _ref3.onScrollChange,
      passedProps = _objectWithoutPropertiesLoose(_ref3, _excluded);
    var ref = useRef(null);
    useDndScrolling(ref, {
      strengthMultiplier: strengthMultiplier,
      verticalStrength: verticalStrength,
      horizontalStrength: horizontalStrength,
      onScrollChange: onScrollChange
    });
    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, passedProps, {
      ref: ref
    }));
  }
  ScrollingComponent.displayName = "Scrolling(" + getDisplayName(WrappedComponent) + ")";
  ScrollingComponent.propTypes = {
    onScrollChange: PropTypes.func,
    verticalStrength: PropTypes.func,
    horizontalStrength: PropTypes.func,
    strengthMultiplier: PropTypes.number
  };
  ScrollingComponent.defaultProps = defaultOptions;
  return hoist(ScrollingComponent, WrappedComponent);
}